import { useQueries, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useMemo } from 'react'

import { getIpfsUrl } from '@/helpers'
import { queryKeys } from '@/queries/keys'
import { NftMetadata } from '@/types'

const METADATA_TIMEOUT_MS = 5_000

function nftMetadataQuery(url: string | null) {
  return {
    queryKey: queryKeys.ipfs.nftMetadata(url),
    enabled: Boolean(url),
    queryFn: async () => {
      if (!url) return
      const { data } = await axios.get<NftMetadata>(url, {
        timeout: METADATA_TIMEOUT_MS,
      })
      return data
    },
    staleTime: Infinity,
    retry: 1,
    retryDelay: 500,
    refetchOnWindowFocus: false,
  }
}

export function useNftMetadataMap(uris: string[]) {
  const uniqueUris = useMemo(() => Array.from(new Set(uris)), [uris])
  const queries = uniqueUris.map(uri => nftMetadataQuery(getIpfsUrl(uri)))

  const results = useQueries({ queries })

  return useMemo(() => {
    const map = new Map<string, NftMetadata | undefined>()
    for (let i = 0; i < uniqueUris.length; i += 1) {
      map.set(uniqueUris[i]!, results[i]?.data as NftMetadata | undefined)
    }
    return map
  }, [uniqueUris, results])
}

export function useNftMetadata(uri: string | undefined) {
  const url = useMemo(() => getIpfsUrl(uri), [uri])
  return useQuery<NftMetadata | undefined>(nftMetadataQuery(url))
}
